import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
// Define types for our component
interface JsonToCsharpProps {}

// C# generation options
type CsharpOptions = {
  namespace: string;
  useProperties: boolean;
  useDataAnnotations: boolean;
  usePascalCase: boolean;
  generateConstructor: boolean;
  accessModifier: 'public' | 'internal' | 'private';
  indentationSpaces: number;
}

// Support for different naming conventions
const toPascalCase = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/[-_](\w)/g, (_, c) => c.toUpperCase())
    .replace(/^\w/, c => c.toUpperCase());
};

const toCamelCase = (str: string): string => {
  if (!str) return '';
  const pascal = toPascalCase(str);
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
};

const JsonToCsharp: React.FC<JsonToCsharpProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the C# output
  const [csharpOutput, setCsharpOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the root class name
  const [rootClassName, setRootClassName] = useState<string>('RootObject');
  // State for the namespace
  const [namespace, setNamespace] = useState<string>('MyNamespace');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isCodeGenerated, setIsCodeGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for C# options
  const [csharpOptions, setCsharpOptions] = useState<CsharpOptions>({
    namespace: 'MyNamespace',
    useProperties: true,
    useDataAnnotations: true,
    usePascalCase: true,
    generateConstructor: false,
    accessModifier: 'public',
    indentationSpaces: 4
  });

  // Define sample data for example
 

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsCodeGenerated(false);
  };

  // Handle root class name change
  const handleRootClassNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRootClassName(e.target.value);
  };

  // Handle namespace change
  const handleNamespaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNamespace(e.target.value);
    setCsharpOptions({...csharpOptions, namespace: e.target.value});
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsCodeGenerated(false);
    setCsharpOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setCsharpOutput('');
    setError('');
    setRootClassName('RootObject');
    setNamespace('MyNamespace');
    setCsharpOptions({
      namespace: 'MyNamespace',
      useProperties: true,
      useDataAnnotations: true,
      usePascalCase: true,
      generateConstructor: false,
      accessModifier: 'public',
      indentationSpaces: 4
    });
    setIsCodeGenerated(false);
  };

  // Handle change in C# options
  const handleOptionChange = (option: keyof CsharpOptions, value: any) => {
    setCsharpOptions({...csharpOptions, [option]: value});
  };

  // Get C# type for a JavaScript value
  const getCSharpType = (value: any, isArray = false): string => {
    if (value === null || value === undefined) return isArray ? "List<object>" : "object";
    
    const type = typeof value;
    
    switch (type) {
      case 'string':
        return isArray ? "List<string>" : "string";
      case 'number':
        // Check if the number is an integer
        return isArray 
          ? Number.isInteger(value) ? "List<int>" : "List<double>" 
          : Number.isInteger(value) ? "int" : "double";
      case 'boolean':
        return isArray ? "List<bool>" : "bool";
      case 'object':
        if (Array.isArray(value)) {
          if (value.length === 0) return "List<object>";
          return `List<${getCSharpType(value[0])}>`;
        }
        return isArray ? `List<${getClassName(value)}>` : getClassName(value);
      default:
        return isArray ? "List<object>" : "object";
    }
  };

  // Get a class name for an object
  const getClassName = (obj: any): string => {
    if (!obj) return "Object";
    
    // Try to infer a meaningful name from object properties
    if (obj.name && typeof obj.name === 'string') {
      return toPascalCase(obj.name) + "Object";
    }
    if (obj.type && typeof obj.type === 'string') {
      return toPascalCase(obj.type) + "Object";
    }
    if (obj.title && typeof obj.title === 'string') {
      return toPascalCase(obj.title) + "Object";
    }
    
    // Default to a generic name
    return "CustomObject";
  };

  // Generate indentation
  const getIndent = (level: number): string => {
    return ' '.repeat(csharpOptions.indentationSpaces * level);
  };

  // Generate C# property/field declaration
  const generatePropertyOrField = (
    name: string,
    type: string,
    indentLevel: number,
    useJsonProperty: boolean
  ): string => {
    const indent = getIndent(indentLevel);
    const propertyName = csharpOptions.usePascalCase ? toPascalCase(name) : name;
    
    let result = '';
    
    // Add JsonProperty attribute if requested
    if (useJsonProperty && csharpOptions.useDataAnnotations) {
      result += `${indent}[JsonProperty("${name}")]\n`;
    }
    
    // Add property or field
    if (csharpOptions.useProperties) {
      result += `${indent}${csharpOptions.accessModifier} ${type} ${propertyName} { get; set; }`;
    } else {
      result += `${indent}${csharpOptions.accessModifier} ${type} ${propertyName};`;
    }
    
    return result;
  };

  // Generate C# class
  const generateClass = (
    obj: any,
    className: string,
    indentLevel: number,
    processedClasses: Set<string>,
    classDefinitions: string[] = []
  ): string => {
    if (processedClasses.has(className)) {
      return ''; // Already processed this class
    }
    
    processedClasses.add(className);
    
    const indent = getIndent(indentLevel);
    let result = `${indent}${csharpOptions.accessModifier} class ${className}\n${indent}{\n`;
    
    // Add properties
    const keys = Object.keys(obj);
    
    // Create constructor if requested
    if (csharpOptions.generateConstructor && keys.length > 0) {
      result += `${getIndent(indentLevel + 1)}public ${className}()\n${getIndent(indentLevel + 1)}{\n`;
      
      // Initialize collections
      for (const key of keys) {
        const value = obj[key];
        if (Array.isArray(value)) {
          const propertyName = csharpOptions.usePascalCase ? toPascalCase(key) : key;
          result += `${getIndent(indentLevel + 2)}${propertyName} = new ${getCSharpType(value)}();\n`;
        }
      }
      
      result += `${getIndent(indentLevel + 1)}}\n\n`;
    }
    
    // Generate properties
    for (const key of keys) {
      const value = obj[key];
      let type = getCSharpType(value);
      
      // For arrays, we need to check the type of the first element
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const firstItem = value[0];
          
          // If it's an object, we need to create a new class for it
          if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
            const itemClassName = toPascalCase(key.replace(/s$/, ''));  // Remove trailing 's' and PascalCase
            
            // Generate the class for the array items
            generateClass(firstItem, itemClassName, indentLevel, processedClasses, classDefinitions);
            
            type = `List<${itemClassName}>`;
          }
        }
      } 
      // For objects, we need to create a new class
      else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        
        // Generate the class for the object
        generateClass(value, nestedClassName, indentLevel, processedClasses, classDefinitions);
        
        type = nestedClassName;
      }
      
      result += generatePropertyOrField(key, type, indentLevel + 1, true) + '\n';
    }
    
    result += `${indent}}`;
    
    // Add class to the list of class definitions
    classDefinitions.push(result);
    
    return classDefinitions.join('\n\n');
  };

  // Generate the full C# code with namespace and using statements
  const generateFullCSharpCode = (jsonObj: any): string => {
    const processedClasses = new Set<string>();
    const classDefinitions: string[] = [];
    
    // Generate the root class and all nested classes
    generateClass(jsonObj, rootClassName, 1, processedClasses, classDefinitions);
    
    // Combine all class definitions
    const classesCode = classDefinitions.join('\n\n');
    
    // Build the full C# code
    let fullCode = 'using System;\n';
    fullCode += 'using System.Collections.Generic;\n';
    
    if (csharpOptions.useDataAnnotations) {
      fullCode += 'using Newtonsoft.Json;\n';
    }
    
    fullCode += '\n';
    fullCode += `namespace ${csharpOptions.namespace}\n`;
    fullCode += '{\n';
    fullCode += classesCode;
    fullCode += '\n}';
    
    return fullCode;
  };

  // Convert JSON to C# classes
  const convertJsonToCsharp = () => {
    setError('');
    setIsLoading(true);
    setIsCodeGenerated(false);
    setCsharpOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      const csharpCode = generateFullCSharpCode(jsonData);
      
      setIsCodeGenerated(true);
      setCsharpOutput(csharpCode);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating C# code: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy C# code to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(csharpOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download C# file
  const downloadCsharp = () => {
    const blob = new Blob([csharpOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${rootClassName}.cs`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to C# Class Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to C# classes. Paste your JSON data below to generate strongly-typed C# class definitions.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="rootClassName" className="block text-sm font-medium text-gray-700 mb-1">
                Root Class Name
              </label>
              <input
                type="text"
                id="rootClassName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={rootClassName}
                onChange={handleRootClassNameChange}
                placeholder="Enter root class name"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="namespace" className="block text-sm font-medium text-gray-700 mb-1">
                Namespace
              </label>
              <input
                type="text"
                id="namespace"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={namespace}
                onChange={handleNamespaceChange}
                placeholder="Enter namespace"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useProperties"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={csharpOptions.useProperties}
                    onChange={(e) => handleOptionChange('useProperties', e.target.checked)}
                  />
                  <label htmlFor="useProperties" className="ml-2 block text-sm text-gray-700">
                    Use Properties (instead of fields)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useDataAnnotations"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={csharpOptions.useDataAnnotations}
                    onChange={(e) => handleOptionChange('useDataAnnotations', e.target.checked)}
                  />
                  <label htmlFor="useDataAnnotations" className="ml-2 block text-sm text-gray-700">
                    Use JsonProperty Attributes
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="usePascalCase"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={csharpOptions.usePascalCase}
                    onChange={(e) => handleOptionChange('usePascalCase', e.target.checked)}
                  />
                  <label htmlFor="usePascalCase" className="ml-2 block text-sm text-gray-700">
                    Use PascalCase for Properties
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateConstructor"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={csharpOptions.generateConstructor}
                    onChange={(e) => handleOptionChange('generateConstructor', e.target.checked)}
                  />
                  <label htmlFor="generateConstructor" className="ml-2 block text-sm text-gray-700">
                    Generate Constructor (initialize collections)
                  </label>
                </div>
                
                <div>
                  <label htmlFor="accessModifier" className="block text-sm font-medium text-gray-700 mb-1">
                    Access Modifier
                  </label>
                  <select
                    id="accessModifier"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={csharpOptions.accessModifier}
                    onChange={(e) => handleOptionChange('accessModifier', e.target.value)}
                  >
                    <option value="public">public</option>
                    <option value="internal">internal</option>
                    <option value="private">private</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="indentationSpaces" className="block text-sm font-medium text-gray-700 mb-1">
                    Indentation Spaces
                  </label>
                  <input
                    type="number"
                    id="indentationSpaces"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={csharpOptions.indentationSpaces}
                    onChange={(e) => handleOptionChange('indentationSpaces', parseInt(e.target.value) || 4)}
                    min={1}
                    max={8}
                  />
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToCsharp}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to C#'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              C# Output
            </label>
            
            {isCodeGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadCsharp}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isCodeGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  <p>
                    C# code will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isCodeGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="csharpOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={csharpOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isCodeGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">C# Classes Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated code includes proper C# classes with properties and JSON attributes. You can copy it or download it as a .cs file.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to C# Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Type Inference</strong> - Automatically determines C# types from JSON values</li>
              <li><strong>Nested Classes</strong> - Creates separate classes for complex nested objects</li>
              <li><strong>Collection Support</strong> - Properly handles arrays as List&lt;T&gt; types</li>
              <li><strong>JSON Attributes</strong> - Adds JsonProperty attributes for proper serialization</li>
              <li><strong>Naming Conventions</strong> - Options to use PascalCase or maintain original property names</li>
              <li><strong>Customization</strong> - Control over access modifiers, indentation, and other code style preferences</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Give a meaningful name to your root class that reflects the data it represents</li>
              <li>For arrays, the converter will try to infer the type from the first element, so ensure your sample JSON includes representative data</li>
              <li>If your JSON structure is complex, consider breaking it into smaller, logical parts</li>
              <li>The generated C# code is ready to use with Newtonsoft.Json for serialization/deserialization</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">C# Code Usage</h3>
            <p className="text-gray-600">
              Once you've generated your C# classes, you can use them with JSON serialization libraries:
            </p>
            <div className="mt-2 bg-gray-100 p-3 rounded-md">
              <code className="text-sm font-mono">
                // Deserialize JSON to C# objects<br/>
                var jsonString = "your JSON string here";<br/>
                var data = JsonConvert.DeserializeObject&lt;RootObject&gt;(jsonString);<br/>
                <br/>
                // Access properties<br/>
                Console.WriteLine(data.PropertyName);<br/>
                <br/>
                // Serialize C# objects back to JSON<br/>
                var newJsonString = JsonConvert.SerializeObject(data, Formatting.Indented);
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToCsharp;